import React, { useEffect, useState } from 'react';
import { auth as authAPI } from '../api';
import { B } from '../constants';

const VALID = /^[a-z0-9_.]{3,20}$/;
const clean = (v) => v.trim().replace(/^@+/, '').toLowerCase();

// Shown once to accounts that still have a generated user<id> handle.
export default function UsernamePrompt({ user, onSaved }) {
  const [value, setValue] = useState(user?.username || '');
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const u = clean(value);
    setError('');
    if (!VALID.test(u)) { setStatus(u ? 'invalid' : null); return undefined; }
    if (u === user?.username) { setStatus('current'); return undefined; }
    let active = true;
    const t = setTimeout(() => {
      authAPI.usernameAvailable(u)
        .then(r => { if (active) setStatus(r?.available ? 'available' : 'taken'); })
        .catch(() => { if (active) setStatus(null); });
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [value, user?.username]);

  const save = async (e) => {
    e.preventDefault();
    const u = clean(value);
    if (!VALID.test(u)) { setError('3-20 characters: letters, numbers, underscores or dots'); return; }
    setSaving(true);
    try {
      const saved = await authAPI.setUsername(u);
      onSaved?.({ username: saved.username, usernameConfirmed: true });
    } catch (err) {
      setError(err.message || 'Could not save that username');
    } finally {
      setSaving(false);
    }
  };

  const hint = {
    invalid: '3-20 characters: letters, numbers, underscores or dots',
    taken: 'That username is taken',
    available: '✓ Available',
    current: 'Keep this one or pick your own',
  }[status];

  return (
    <div role="dialog" aria-modal="true" aria-label="Choose your username"
      style={{ position: 'fixed', inset: 0, zIndex: 10002, background: 'rgba(15,25,35,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <form onSubmit={save} style={{ width: '100%', maxWidth: 400, background: 'white', borderRadius: 18, padding: 24, fontFamily: "'Montserrat',sans-serif", boxSizing: 'border-box' }}>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, margin: '0 0 6px', color: B.night }}>Pick your @username</h2>
        <p style={{ fontSize: 13, color: B.muted, margin: '0 0 16px' }}>This is how other travellers find and follow you.</p>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: B.muted }}>@</span>
          <input aria-label="Username" autoFocus value={value} onChange={e => setValue(e.target.value)} maxLength={21}
            style={{ width: '100%', padding: '12px 14px 12px 30px', borderRadius: 12, border: `1px solid ${status === 'taken' || status === 'invalid' ? '#d64545' : '#e5e0d8'}`, fontSize: 15, boxSizing: 'border-box' }} />
        </div>
        <div style={{ minHeight: 18, fontSize: 12, marginTop: 6, color: error || status === 'taken' || status === 'invalid' ? '#d64545' : B.forest }}>{error || hint}</div>
        <button type="submit" disabled={saving || status === 'taken' || status === 'invalid'}
          style={{ marginTop: 14, width: '100%', padding: 12, borderRadius: 50, border: 'none', background: B.forest, color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save username'}
        </button>
      </form>
    </div>
  );
}
